// Web Push Notification utilities

const VAPID_PUBLIC_KEY = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY || '';

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    throw new Error('This browser does not support notifications');
  }

  return await Notification.requestPermission();
}

export async function subscribeToPush(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service workers are not supported');
  }

  // Validate VAPID key
  if (!VAPID_PUBLIC_KEY || VAPID_PUBLIC_KEY.length < 80) {
    console.error('❌ Invalid VAPID Public Key:', {
      length: VAPID_PUBLIC_KEY?.length || 0,
      key: VAPID_PUBLIC_KEY ? VAPID_PUBLIC_KEY.substring(0, 20) + '...' : 'missing',
    });
    throw new Error('VAPID Public Key is missing or invalid. It should be at least 80 characters long.');
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    
    // Convert VAPID key
    let applicationServerKey: Uint8Array;
    try {
      applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      console.log('✅ VAPID key converted successfully, length:', applicationServerKey.length);
      console.log('🔑 VAPID key first bytes:', Array.from(applicationServerKey.slice(0, 5)));
    } catch (conversionError) {
      console.error('❌ Failed to convert VAPID key:', conversionError);
      throw new Error('Invalid VAPID Public Key format. Check that it is a valid base64 URL-safe string.');
    }
    
    console.log('📡 Attempting to subscribe to push service...');
    console.log('📡 Service worker state:', registration.active?.state || registration.installing?.state || 'unknown');
    console.log('📡 Application server key length:', applicationServerKey.length, 'bytes');
    
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey as any, // TypeScript workaround for Uint8Array
    });
    
    console.log('✅ Push subscription created successfully');
    return subscription;

  } catch (error: any) {
    console.error('❌ Failed to subscribe to push:', error);
    console.error('❌ Error details:', {
      name: error.name,
      message: error.message,
      VAPID_KEY_LENGTH: VAPID_PUBLIC_KEY?.length || 0,
      VAPID_KEY_PREFIX: VAPID_PUBLIC_KEY ? VAPID_PUBLIC_KEY.substring(0, 20) : 'missing',
    });
    throw error; // Re-throw to let caller handle it
  }
}

export async function unsubscribeFromPush(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      return await subscription.unsubscribe();
    }
    
    return false;
  } catch (error) {
    console.error('Failed to unsubscribe from push:', error);
    return false;
  }
}

export function showNotification(title: string, options?: NotificationOptions): void {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, options);
  }
}

// Helper function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  try {
    // Remove any whitespace
    const cleanKey = base64String.trim();
    
    // Add padding if needed
    const padding = '='.repeat((4 - (cleanKey.length % 4)) % 4);
    const base64 = (cleanKey + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const buffer = new ArrayBuffer(rawData.length);
    const outputArray = new Uint8Array(buffer);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    // VAPID public key should be 65 bytes (uncompressed EC point)
    if (outputArray.length !== 65) {
      console.warn(`⚠️ VAPID key length is ${outputArray.length} bytes, expected 65 bytes. This might cause issues.`);
    }

    return outputArray;
  } catch (error) {
    console.error('❌ Error converting VAPID key:', error);
    throw new Error(`Failed to convert VAPID key: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
